/**
 * Hook para gerenciar a blacklist de contatos LGPD
 * Permite adicionar, remover e verificar contatos bloqueados
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { normalizeBrazilianPhone } from '@/lib/phone-utils';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';

// =====================================================
// TIPOS
// =====================================================

/** Entrada na blacklist */
export interface BlacklistEntry {
  id: string;
  phone: string;
  phone_normalized: string;
  name: string | null;
  reason: string;
  source: string;
  blocked_by: string | null;
  created_at: string;
}

/** Motivos de bloqueio padronizados LGPD */
export type BlockReason = 'opt_out' | 'solicitacao_cliente' | 'spam' | 'outro';

/** Labels para os motivos */
export const BLOCK_REASON_LABELS: Record<BlockReason, string> = {
  opt_out: 'Opt-out (descadastro)',
  solicitacao_cliente: 'Solicitação do cliente',
  spam: 'Marcado como spam',
  outro: 'Outro motivo'
};

/** Resultado da verificação de blacklist */
export interface BlacklistCheckResult {
  phone_normalized: string;
  name: string | null;
  reason: string;
}

// =====================================================
// HOOK
// =====================================================

export function useBlacklist() {
  const { currentTenant, user } = useAuth();
  
  // Estados
  const [entries, setEntries] = useState<BlacklistEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  /**
   * Busca entradas da blacklist com paginação
   * @param page - Página atual (0-indexed)
   * @param pageSize - Tamanho da página
   * @param searchQuery - Filtro por telefone ou nome
   */
  const fetchBlacklist = useCallback(async (
    page: number = 0,
    pageSize: number = 50,
    searchQuery: string = ''
  ) => {
    if (!currentTenant?.id) return;
    
    setIsLoading(true);
    try {
      // Query base
      let query = supabase
        .from('contact_blacklist')
        .select('*', { count: 'exact' })
        .eq('tenant_id', currentTenant.id)
        .order('created_at', { ascending: false });
      
      // Aplicar filtro de busca
      if (searchQuery.trim()) {
        query = query.or(`phone.ilike.%${searchQuery}%,name.ilike.%${searchQuery}%`);
      }
      
      // Aplicar paginação
      const from = page * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);
      
      const { data, error, count } = await query;
      
      if (error) throw error;
      
      setEntries(data || []);
      setTotalCount(count || 0);
      
      logger.info('[useBlacklist] Blacklist carregada:', `count=${count}`);
      
    } catch (err) {
      logger.error('[useBlacklist] Erro ao carregar blacklist:', err);
      toast.error('Erro ao carregar blacklist');
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant?.id]);
  
  /**
   * Adiciona um contato à blacklist
   * @param phone - Telefone do contato
   * @param name - Nome opcional
   * @param reason - Motivo do bloqueio
   */
  const addToBlacklist = useCallback(async (
    phone: string,
    name?: string,
    reason: BlockReason = 'opt_out'
  ): Promise<boolean> => {
    if (!currentTenant?.id || !user?.id) {
      toast.error('Usuário ou tenant não identificado');
      return false;
    }
    
    try {
      const phoneNormalized = normalizeBrazilianPhone(phone);
      
      if (!phoneNormalized) {
        toast.error('Telefone inválido');
        return false;
      }
      
      const { error } = await supabase
        .from('contact_blacklist')
        .insert({
          tenant_id: currentTenant.id,
          phone: phone,
          phone_normalized: phoneNormalized,
          name: name || null,
          reason: reason,
          source: 'manual',
          blocked_by: user.id
        });
      
      if (error) {
        // Verificar se é erro de duplicata
        if (error.code === '23505') {
          toast.error('Este telefone já está na blacklist');
          return false;
        }
        throw error;
      }
      
      toast.success('Contato adicionado à blacklist');
      logger.info('[useBlacklist] Contato adicionado:', phoneNormalized);
      
      return true;
      
    } catch (err) {
      logger.error('[useBlacklist] Erro ao adicionar à blacklist:', err);
      toast.error('Erro ao adicionar à blacklist');
      return false;
    }
  }, [currentTenant?.id, user?.id]);
  
  /**
   * Remove um contato da blacklist
   * @param id - ID da entrada
   */
  const removeFromBlacklist = useCallback(async (id: string): Promise<boolean> => {
    if (!currentTenant?.id) {
      toast.error('Tenant não identificado');
      return false;
    }
    
    try {
      const { error } = await supabase
        .from('contact_blacklist')
        .delete()
        .eq('id', id)
        .eq('tenant_id', currentTenant.id);
      
      if (error) throw error;
      
      toast.success('Contato removido da blacklist');
      logger.info('[useBlacklist] Contato removido:', id);
      
      return true;
      
    } catch (err) {
      logger.error('[useBlacklist] Erro ao remover da blacklist:', err);
      toast.error('Erro ao remover da blacklist');
      return false;
    }
  }, [currentTenant?.id]);
  
  /**
   * Verifica quais telefones estão na blacklist (em lote)
   * @param phones - Array de telefones normalizados
   */
  const checkBlacklist = useCallback(async (
    phones: string[]
  ): Promise<Map<string, BlacklistCheckResult>> => {
    if (!currentTenant?.id || phones.length === 0) {
      return new Map();
    }
    
    try {
      const { data, error } = await supabase.rpc('check_blacklist_phones', {
        p_phones: phones,
        p_tenant_hint: currentTenant.id
      });
      
      if (error) throw error;
      
      // Converter para Map para busca rápida
      const resultMap = new Map<string, BlacklistCheckResult>();
      (data || []).forEach((item: BlacklistCheckResult) => {
        resultMap.set(item.phone_normalized, item);
      });
      
      logger.info('[useBlacklist] Verificação de blacklist:', `checked=${phones.length}, found=${resultMap.size}`);
      
      return resultMap;
      
    } catch (err) {
      logger.error('[useBlacklist] Erro ao verificar blacklist:', err);
      return new Map();
    }
  }, [currentTenant?.id]);
  
  /**
   * Importa múltiplos contatos via CSV
   * @param contacts - Array de { phone, name?, reason? }
   */
  const importBlacklistCSV = useCallback(async (
    contacts: Array<{ phone: string; name?: string; reason?: BlockReason }>
  ): Promise<{ imported: number; skipped: number; errors: string[] }> => {
    if (!currentTenant?.id || !user?.id) {
      return { imported: 0, skipped: 0, errors: ['Usuário ou tenant não identificado'] };
    }
    
    let imported = 0;
    let skipped = 0;
    const errors: string[] = [];
    
    try {
      // Processar cada contato
      const entries = contacts.map(contact => {
        const phoneNormalized = normalizeBrazilianPhone(contact.phone);
        
        if (!phoneNormalized) {
          errors.push(`Telefone inválido: ${contact.phone}`);
          return null;
        }
        
        return {
          tenant_id: currentTenant.id,
          phone: contact.phone,
          phone_normalized: phoneNormalized,
          name: contact.name || null,
          reason: contact.reason || 'opt_out',
          source: 'csv_import',
          blocked_by: user.id
        };
      }).filter(Boolean);
      
      if (entries.length === 0) {
        return { imported: 0, skipped: 0, errors };
      }
      
      // Inserir com upsert para ignorar duplicatas
      const { data, error } = await supabase
        .from('contact_blacklist')
        .upsert(entries as any[], {
          onConflict: 'tenant_id,phone_normalized',
          ignoreDuplicates: true
        })
        .select('id');
      
      if (error) throw error;
      
      imported = data?.length || 0;
      skipped = entries.length - imported;
      
      logger.info('[useBlacklist] Importação CSV:', `imported=${imported}, skipped=${skipped}`);
      
      if (imported > 0) {
        toast.success(`${imported} contatos adicionados à blacklist`);
      }
      if (skipped > 0) {
        toast.info(`${skipped} contatos já estavam na blacklist`);
      }
      
      return { imported, skipped, errors };
      
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro desconhecido';
      logger.error('[useBlacklist] Erro na importação CSV:', errorMsg);
      errors.push(errorMsg);
      return { imported, skipped, errors };
    }
  }, [currentTenant?.id, user?.id]);
  
  /**
   * Busca contagem total da blacklist (para badge)
   */
  const fetchBlacklistCount = useCallback(async (): Promise<number> => {
    if (!currentTenant?.id) return 0;
    
    try {
      const { count, error } = await supabase
        .from('contact_blacklist')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', currentTenant.id);
      
      if (error) throw error;
      
      return count || 0;
      
    } catch (err) {
      logger.error('[useBlacklist] Erro ao buscar contagem:', err);
      return 0;
    }
  }, [currentTenant?.id]);
  
  return {
    // Estados
    entries,
    isLoading,
    totalCount,
    
    // Métodos
    fetchBlacklist,
    fetchBlacklistCount,
    addToBlacklist,
    removeFromBlacklist,
    checkBlacklist,
    importBlacklistCSV
  };
}
